/**
 * Persists candidate screening progress on the device (no login).
 * Keyed by screening id from the public apply URL.
 */

const STORAGE_VERSION = 1;

const storageKey = (screeningId) => `tc_screening_candidate_${screeningId}`;

const emptyResumeDraft = () => ({
  noticePeriod: '',
  currentCtc: '',
  expectedCtc: '',
  age: '',
});

export function readApplySession(screeningId) {
  if (!screeningId || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(storageKey(screeningId));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || data.version !== STORAGE_VERSION || data.screeningId !== screeningId) return null;
    return data;
  } catch {
    return null;
  }
}

function writeApplySession(screeningId, next) {
  if (!screeningId || typeof window === 'undefined') return;
  const payload = {
    ...next,
    version: STORAGE_VERSION,
    screeningId,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(storageKey(screeningId), JSON.stringify(payload));
}

function normalizeEmail(e) {
  return (e || '').trim().toLowerCase();
}

/**
 * Call after basic details + timer start succeed, before navigating to /resume.
 */
export function saveResumeStageAfterBasic(screeningId, basicDetails) {
  if (!screeningId || !basicDetails) return;
  const email = normalizeEmail(basicDetails.email);
  const prev = readApplySession(screeningId);
  const sameCandidate = prev?.basicDetails && normalizeEmail(prev.basicDetails.email) === email;

  writeApplySession(screeningId, {
    stage: 'resume',
    basicDetails: {
      name: (basicDetails.name || '').trim(),
      email: (basicDetails.email || '').trim(),
      phone: (basicDetails.phone || '').trim(),
      hasAssessment: !!basicDetails.hasAssessment,
      hasInterview: !!basicDetails.hasInterview,
    },
    resumeDraft: sameCandidate && prev.resumeDraft
      ? { ...emptyResumeDraft(), ...prev.resumeDraft }
      : emptyResumeDraft(),
    resumeFileMeta: sameCandidate ? prev.resumeFileMeta || null : null,
  });
}

/** Persist notice period / CTC / age while on the resume step. */
export function patchResumeDraft(screeningId, partial) {
  const prev = readApplySession(screeningId);
  if (!prev || prev.stage !== 'resume') return;
  writeApplySession(screeningId, {
    ...prev,
    resumeDraft: { ...emptyResumeDraft(), ...prev.resumeDraft, ...partial },
  });
}

export function setResumeFileMeta(screeningId, file) {
  const prev = readApplySession(screeningId);
  if (!prev || prev.stage !== 'resume') return;
  if (!file) {
    writeApplySession(screeningId, { ...prev, resumeFileMeta: null });
    return;
  }
  writeApplySession(screeningId, {
    ...prev,
    resumeFileMeta: {
      name: file.name,
      size: file.size,
      lastModified: file.lastModified,
    },
  });
}

/** After successful resume submit — next step is hub or external assessment flow. */
export function markHubStage(screeningId, email, name) {
  const prev = readApplySession(screeningId) || {};
  const e = (email || '').trim();
  const n = (name || '').trim();
  writeApplySession(screeningId, {
    stage: 'hub',
    basicDetails: prev.basicDetails || null,
    hubEmail: e || prev.hubEmail || (prev.basicDetails?.email || '').trim(),
    hubName: n || prev.hubName || (prev.basicDetails?.name || '').trim(),
  });
}

export function clearApplySession(screeningId) {
  if (!screeningId || typeof window === 'undefined') return;
  localStorage.removeItem(storageKey(screeningId));
}

/** The Application Form step's URL, carrying the identity the later steps expect. */
export function applicationFormPath(screeningId, token, { email, name } = {}) {
  const params = new URLSearchParams();
  params.set('token', token);
  if (email && email.trim()) params.set('email', email.trim());
  if (name && name.trim()) params.set('name', name.trim());
  return `/apply/${screeningId}/application-form?${params.toString()}`;
}
